/**
 * GIF 첫 프레임을 정적 이미지 blob URL로 추출하는 유틸리티
 *
 * canvas.drawImage(<img>)로 첫 프레임을 캡처하여
 * 브라우저의 GIF 자동 애니메이션을 방지한다.
 */

// 에셋 ID → 정적 프레임 blob URL 캐시
const gifFirstFrameCache = new Map<string, string>();
// 에셋 ID → 진행 중인 추출 Promise (중복 방지)
const gifFirstFrameInFlight = new Map<string, Promise<string | null>>();
const ANIMATED_IMAGE_MIME_TYPES = ['image/gif', 'image/webp'] as const;

async function extractFirstFrame(gifBlobUrl: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(URL.createObjectURL(blob));
          } else {
            resolve(null);
          }
        }, 'image/png');
      } catch {
        resolve(null);
      }
    };

    img.onerror = () => resolve(null);
    img.src = gifBlobUrl;
  });
}

/**
 * GIF 에셋의 첫 프레임을 정적 이미지 URL로 가져온다.
 * 결과는 캐시되어 같은 에셋에 대해 반복 추출하지 않는다.
 */
export async function getGifFirstFrame(
  assetId: string,
  gifBlobUrl: string,
): Promise<string | null> {
  const cached = gifFirstFrameCache.get(assetId);
  if (cached) return cached;

  const inFlight = gifFirstFrameInFlight.get(assetId);
  if (inFlight) return inFlight;

  const promise = extractFirstFrame(gifBlobUrl).then((url) => {
    gifFirstFrameInFlight.delete(assetId);
    if (url) {
      gifFirstFrameCache.set(assetId, url);
    }
    return url;
  });

  gifFirstFrameInFlight.set(assetId, promise);
  return promise;
}

export function getCachedGifFirstFrame(assetId: string): string | null {
  return gifFirstFrameCache.get(assetId) ?? null;
}

export function isAnimatedImageMimeType(
  mimeType: string | null | undefined,
): boolean {
  if (!mimeType) return false;
  const normalized = mimeType.toLowerCase().split(';', 1)[0]?.trim();
  return normalized
    ? ANIMATED_IMAGE_MIME_TYPES.includes(
        normalized as (typeof ANIMATED_IMAGE_MIME_TYPES)[number],
      )
    : false;
}

/**
 * 캐시된 모든 GIF 첫 프레임 blob URL을 revoke하고 캐시를 비운다.
 * 에디터 세션 종료 시 호출하여 메모리 누수를 방지한다.
 */
export function cleanupGifFirstFrameCache(): void {
  gifFirstFrameCache.forEach((url) => {
    URL.revokeObjectURL(url);
  });
  gifFirstFrameCache.clear();
  gifFirstFrameInFlight.clear();
}
